/**
 * Alta y edición de producto.
 *
 * Está armado para capturar rápido, porque el catálogo se construye desde
 * cero y alguien va a pasar horas en esta pantalla:
 *
 *   · Sólo tres campos son obligatorios: nombre, precio y categoría.
 *   · Los datos del SAT y del inventario viven plegados, con valores por
 *     defecto que sirven. Nadie tiene que saberse la clave 01010101 para
 *     poder vender una torta.
 *   · Al guardar y cerrar, el foco vuelve al nombre, para encadenar altas.
 */
import { useEffect, useRef, useState } from 'react';
import { api, API_BASE, ErrorApi } from '../lib/api';
import { sesion } from '../lib/sesion';
import type { Categoria, EstadoProducto, Producto } from '../lib/tipos';

interface SucursalBasica {
  id: number;
  nombre: string;
  tipo: 'restaurante' | 'abarrotes';
}

interface Props {
  sucursalId: number;
  sucursales?: SucursalBasica[];
  producto: Producto | null;
  categorias: Categoria[];
  /** Código escaneado que no existía. Llega al alta ya escrito. */
  codigoNuevo?: string;
  tipoPorDefecto: 'platillo' | 'abarrote';
  alGuardar: () => void;
  alCerrar: () => void;
}

/**
 * A qué otros restaurantes, además del dueño, se comparte este platillo.
 * Sólo tiene sentido para platillos sin control de inventario: uno con
 * existencia real mezclaría el stock de dos sucursales bajo un solo número.
 */
function VisibilidadEntreSucursales({
  sucursalId,
  productoId,
  destinos,
}: {
  sucursalId: number;
  productoId: number;
  destinos: SucursalBasica[];
}) {
  const [seleccion, setSeleccion] = useState<number[]>([]);
  const [cargando, setCargando] = useState(true);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [ok, setOk] = useState(false);

  useEffect(() => {
    api<number[]>(`/catalogo/productos/${productoId}/visibilidad?sucursalId=${sucursalId}`)
      .then(setSeleccion)
      .catch(() => setSeleccion([]))
      .finally(() => setCargando(false));
  }, [productoId, sucursalId]);

  function alternar(id: number) {
    setOk(false);
    setSeleccion((prev) => (prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id]));
  }

  async function guardar() {
    try {
      setGuardando(true);
      setError(null);
      await api(`/catalogo/productos/${productoId}/visibilidad?sucursalId=${sucursalId}`, {
        method: 'PUT',
        body: { sucursales: seleccion },
      });
      setOk(true);
    } catch (err) {
      setError(err instanceof ErrorApi ? err.message : 'No se pudo guardar la visibilidad.');
    } finally {
      setGuardando(false);
    }
  }

  if (!destinos.length) return null;

  return (
    <div className="flex flex-col gap-3 rounded-lg bg-cal p-4">
      <span className="text-sm font-semibold">
        También visible en…
        <span className="block text-xs text-salvia">
          Aparece en el menú de esas sucursales sin volver a capturarlo. No cambies esto en un
          producto que controla existencia: el stock es de esta sucursal, no se comparte.
        </span>
      </span>
      {cargando ? (
        <p className="text-xs text-salvia">Cargando…</p>
      ) : (
        <>
          <div className="flex flex-wrap gap-2">
            {destinos.map((s) => (
              <label
                key={s.id}
                className={`flex cursor-pointer items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-xs font-semibold ${
                  seleccion.includes(s.id)
                    ? 'border-nopal bg-nopal-claro text-nopal-oscuro'
                    : 'border-salvia/20 text-salvia'
                }`}
              >
                <input
                  type="checkbox"
                  checked={seleccion.includes(s.id)}
                  onChange={() => alternar(s.id)}
                  className="accent-nopal"
                />
                {s.nombre}
              </label>
            ))}
          </div>
          {error && <p className="text-xs font-semibold text-barro-oscuro">{error}</p>}
          <div className="flex items-center gap-3">
            <button
              type="button"
              disabled={guardando}
              onClick={guardar}
              className="self-start rounded-lg border border-nopal bg-white px-4 py-1.5 text-xs font-bold text-nopal-oscuro hover:bg-nopal-claro"
            >
              {guardando ? 'Guardando…' : 'Guardar visibilidad'}
            </button>
            {ok && <span className="text-xs font-semibold text-nopal-oscuro">Guardado.</span>}
          </div>
        </>
      )}
    </div>
  );
}

interface Borrador {
  nombre: string;
  precio: string;
  costo: string;
  categoria_id: string;
  codigo_barras: string;
  descripcion: string;
  imagen_url: string;
  unidad: string;
  controla_stock: boolean;
  por_peso: boolean;
  stock_minimo: string;
  stockInicial: string;
  minutos_prep: string;
  clave_prod_serv: string;
  clave_unidad: string;
  /** Se conserva tal cual al editar: guardar el precio de un platillo
   *  agotado no debe devolverlo al menú por un efecto secundario. */
  estado: EstadoProducto;
}

function vacio(codigoNuevo?: string): Borrador {
  return {
    nombre: '',
    precio: '',
    costo: '',
    categoria_id: '',
    codigo_barras: codigoNuevo ?? '',
    descripcion: '',
    imagen_url: '',
    unidad: 'Pieza',
    controla_stock: true,
    por_peso: false,
    stock_minimo: '',
    stockInicial: '',
    minutos_prep: '',
    clave_prod_serv: '01010101',
    clave_unidad: 'H87',
    estado: 'borrador',
  };
}

function desdeProducto(p: Producto): Borrador {
  return {
    nombre: p.nombre,
    precio: p.precio,
    costo: p.costo,
    categoria_id: p.categoria_id ? String(p.categoria_id) : '',
    codigo_barras: p.codigo_barras ?? '',
    descripcion: p.descripcion ?? '',
    imagen_url: p.imagen_url ?? '',
    unidad: p.unidad,
    controla_stock: !!p.controla_stock,
    por_peso: !!p.por_peso,
    stock_minimo: p.stock_minimo,
    stockInicial: '',
    minutos_prep: p.minutos_prep ? String(p.minutos_prep) : '',
    clave_prod_serv: p.clave_prod_serv,
    clave_unidad: p.clave_unidad,
    estado: p.estado,
  };
}

export function EditorProducto({
  sucursalId,
  sucursales = [],
  producto,
  categorias,
  codigoNuevo,
  tipoPorDefecto,
  alGuardar,
  alCerrar,
}: Props) {
  const editando = !!producto;
  const [b, setB] = useState<Borrador>(producto ? desdeProducto(producto) : vacio(codigoNuevo));
  const [avanzado, setAvanzado] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [guardando, setGuardando] = useState(false);
  const [subiendoImagen, setSubiendoImagen] = useState(false);
  const inputImagen = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setB(producto ? desdeProducto(producto) : vacio(codigoNuevo));
    setError(null);
  }, [producto, codigoNuevo]);

  // Escape cierra: en tablet con teclado externo es el reflejo de cualquiera.
  useEffect(() => {
    const alTeclear = (e: KeyboardEvent) => e.key === 'Escape' && alCerrar();
    window.addEventListener('keydown', alTeclear);
    return () => window.removeEventListener('keydown', alTeclear);
  }, [alCerrar]);

  function campo<K extends keyof Borrador>(k: K, v: Borrador[K]) {
    setB((prev) => ({ ...prev, [k]: v }));
  }

  /**
   * Sube el archivo aparte del resto del formulario: así una foto grande no
   * bloquea la captura de precio y nombre, y el fetch no pasa por `api()`
   * porque ésta siempre manda JSON, no multipart.
   */
  async function subirImagen(archivo: File) {
    setSubiendoImagen(true);
    setError(null);
    try {
      const cuerpo = new FormData();
      cuerpo.append('imagen', archivo);
      const resp = await fetch(`${API_BASE}/api/catalogo/productos/imagen?sucursalId=${sucursalId}`, {
        method: 'POST',
        headers: sesion.token ? { Authorization: `Bearer ${sesion.token}` } : undefined,
        body: cuerpo,
      });
      const json = await resp.json().catch(() => null);
      if (!resp.ok || !json?.ok) throw new Error(json?.mensaje ?? 'No se pudo subir la imagen.');
      campo('imagen_url', json.datos.url as string);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo subir la imagen.');
    } finally {
      setSubiendoImagen(false);
      if (inputImagen.current) inputImagen.current.value = '';
    }
  }

  function urlImagen(ruta: string) {
    return ruta.startsWith('http') || ruta.startsWith('/menu/') ? ruta : `${API_BASE}${ruta}`;
  }

  async function guardar(publicar: boolean) {
    if (!b.nombre.trim()) return setError('Escribe el nombre del producto.');
    if (b.precio === '' || Number(b.precio) < 0) return setError('Escribe un precio válido.');

    setGuardando(true);
    setError(null);

    const cuerpo: Record<string, unknown> = {
      tipo: producto?.tipo ?? tipoPorDefecto,
      nombre: b.nombre.trim(),
      descripcion: b.descripcion.trim() || null,
      imagen_url: b.imagen_url.trim() || null,
      categoria_id: b.categoria_id ? Number(b.categoria_id) : null,
      codigo_barras: b.codigo_barras.trim() || null,
      precio: Number(b.precio),
      costo: b.costo === '' ? 0 : Number(b.costo),
      unidad: b.unidad,
      clave_prod_serv: b.clave_prod_serv,
      clave_unidad: b.clave_unidad,
      por_peso: b.por_peso,
      controla_stock: b.controla_stock,
      stock_minimo: b.stock_minimo === '' ? 0 : Number(b.stock_minimo),
      minutos_prep: b.minutos_prep ? Number(b.minutos_prep) : null,
      estado: publicar ? 'publicado' : b.estado,
    };

    // El stock inicial sólo tiene sentido al crear: después se mueve con un
    // ajuste, que deja constancia de quién lo cambió y por qué.
    if (!editando && b.stockInicial !== '') cuerpo.stockInicial = Number(b.stockInicial);

    try {
      if (editando) {
        await api(`/catalogo/productos/${producto!.id}?sucursalId=${sucursalId}`, {
          method: 'PATCH',
          body: cuerpo,
        });
      } else {
        await api(`/catalogo/productos?sucursalId=${sucursalId}`, { method: 'POST', body: cuerpo });
      }
      alGuardar();
    } catch (err) {
      setError(err instanceof ErrorApi ? err.message : 'No se pudo guardar.');
    } finally {
      setGuardando(false);
    }
  }

  const esPlatillo = (producto?.tipo ?? tipoPorDefecto) === 'platillo';

  return (
    <div className="fixed inset-0 z-40 flex items-end justify-center bg-tinta/40 sm:items-center">
      <div className="max-h-[92vh] w-full max-w-lg overflow-y-auto rounded-t-2xl bg-white p-6 sm:rounded-2xl">
        <div className="mb-5 flex items-baseline justify-between">
          <h2 className="text-2xl font-bold">{editando ? 'Editar producto' : 'Nuevo producto'}</h2>
          <button onClick={alCerrar} className="text-sm text-salvia underline">
            Cerrar
          </button>
        </div>

        {codigoNuevo && !editando && (
          <p className="mb-4 rounded-lg bg-nopal-claro px-4 py-3 text-sm text-nopal-oscuro">
            Código <span className="font-dato">{codigoNuevo}</span> sin registrar. Complétalo y
            queda listo para venderse.
          </p>
        )}

        <div className="flex flex-col gap-4">
          <label className="flex flex-col gap-1">
            <span className="text-sm font-semibold">Nombre</span>
            <input
              autoFocus
              className="campo"
              value={b.nombre}
              onChange={(e) => campo('nombre', e.target.value)}
              placeholder={esPlatillo ? 'Torta de milanesa' : 'Azúcar 1 kg'}
            />
          </label>

          <div className="flex flex-col gap-1">
            <span className="text-sm font-semibold">
              Foto del platillo <span className="font-normal text-salvia">(opcional)</span>
            </span>
            <div className="flex items-center gap-3">
              {b.imagen_url ? (
                <img
                  src={urlImagen(b.imagen_url)}
                  alt=""
                  className="h-20 w-20 rounded-lg object-cover"
                />
              ) : (
                <div className="flex h-20 w-20 items-center justify-center rounded-lg border border-dashed border-salvia/30 text-xs text-salvia">
                  Sin foto
                </div>
              )}
              <div className="flex flex-col gap-1.5">
                <input
                  ref={inputImagen}
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  className="hidden"
                  onChange={(e) => {
                    const archivo = e.target.files?.[0];
                    if (archivo) void subirImagen(archivo);
                  }}
                />
                <button
                  type="button"
                  disabled={subiendoImagen}
                  onClick={() => inputImagen.current?.click()}
                  className="btn-neutro min-h-0 py-2 text-xs"
                >
                  {subiendoImagen ? 'Subiendo…' : b.imagen_url ? 'Cambiar foto' : 'Subir foto'}
                </button>
                {b.imagen_url && (
                  <button
                    type="button"
                    onClick={() => campo('imagen_url', '')}
                    className="text-xs text-salvia underline"
                  >
                    Quitar
                  </button>
                )}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <label className="flex flex-col gap-1">
              <span className="text-sm font-semibold">Precio de venta</span>
              <input
                className="campo num"
                inputMode="decimal"
                value={b.precio}
                onChange={(e) => campo('precio', e.target.value)}
                placeholder="0.00"
              />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-sm font-semibold">Costo</span>
              <input
                className="campo num"
                inputMode="decimal"
                value={b.costo}
                onChange={(e) => campo('costo', e.target.value)}
                placeholder="0.00"
              />
            </label>
          </div>

          <label className="flex flex-col gap-1">
            <span className="text-sm font-semibold">Categoría</span>
            <select
              className="campo"
              value={b.categoria_id}
              onChange={(e) => campo('categoria_id', e.target.value)}
            >
              <option value="">Sin categoría</option>
              {categorias.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.nombre}
                </option>
              ))}
            </select>
          </label>

          {!esPlatillo && (
            <label className="flex flex-col gap-1">
              <span className="text-sm font-semibold">Código de barras</span>
              <input
                className="campo font-dato"
                value={b.codigo_barras}
                onChange={(e) => campo('codigo_barras', e.target.value)}
                placeholder="Escanea o escribe"
              />
            </label>
          )}

          {esPlatillo && (
            <label className="flex flex-col gap-1">
              <span className="text-sm font-semibold">
                Minutos de preparación <span className="font-normal text-salvia">(opcional)</span>
              </span>
              <input
                className="campo num"
                inputMode="numeric"
                value={b.minutos_prep}
                onChange={(e) => campo('minutos_prep', e.target.value)}
                placeholder="12"
              />
              <span className="text-xs text-salvia">
                Con esto la pantalla de cocina sabe cuándo una orden va tarde.
              </span>
            </label>
          )}

          <button
            onClick={() => setAvanzado(!avanzado)}
            className="self-start text-sm text-salvia underline"
          >
            {avanzado ? 'Ocultar' : 'Mostrar'} inventario y datos de factura
          </button>

          {avanzado && (
            <div className="flex flex-col gap-4 rounded-lg bg-cal p-4">
              <label className="flex items-center gap-3">
                <input
                  type="checkbox"
                  className="h-5 w-5 accent-nopal"
                  checked={b.controla_stock}
                  onChange={(e) => campo('controla_stock', e.target.checked)}
                />
                <span className="text-sm">
                  Descontar del inventario al vender
                  <span className="block text-xs text-salvia">
                    Desactívalo en guisados y platillos preparados al momento.
                  </span>
                </span>
              </label>

              {b.controla_stock && (
                <div className="grid grid-cols-2 gap-3">
                  {!editando && (
                    <label className="flex flex-col gap-1">
                      <span className="text-sm font-semibold">Existencia inicial</span>
                      <input
                        className="campo num"
                        inputMode="decimal"
                        value={b.stockInicial}
                        onChange={(e) => campo('stockInicial', e.target.value)}
                        placeholder="0"
                      />
                    </label>
                  )}
                  <label className="flex flex-col gap-1">
                    <span className="text-sm font-semibold">Mínimo</span>
                    <input
                      className="campo num"
                      inputMode="decimal"
                      value={b.stock_minimo}
                      onChange={(e) => campo('stock_minimo', e.target.value)}
                      placeholder="0"
                    />
                  </label>
                </div>
              )}

              <label className="flex items-center gap-3">
                <input
                  type="checkbox"
                  className="h-5 w-5 accent-nopal"
                  checked={b.por_peso}
                  onChange={(e) => campo('por_peso', e.target.checked)}
                />
                <span className="text-sm">Se vende por peso (báscula)</span>
              </label>

              <div className="grid grid-cols-2 gap-3">
                <label className="flex flex-col gap-1">
                  <span className="text-sm font-semibold">Unidad</span>
                  <input
                    className="campo"
                    value={b.unidad}
                    onChange={(e) => campo('unidad', e.target.value)}
                  />
                </label>
                <label className="flex flex-col gap-1">
                  <span className="text-sm font-semibold">Clave SAT</span>
                  <input
                    className="campo font-dato"
                    value={b.clave_prod_serv}
                    onChange={(e) => campo('clave_prod_serv', e.target.value)}
                  />
                </label>
              </div>
              <p className="text-xs text-salvia">
                La clave SAT sólo importa al facturar. El valor que trae sirve para empezar y se
                puede afinar después sin volver a capturar el producto.
              </p>
            </div>
          )}

          {editando &&
            producto &&
            !producto.compartido &&
            producto.tipo === 'platillo' &&
            !b.controla_stock && (
              <VisibilidadEntreSucursales
                sucursalId={sucursalId}
                productoId={producto.id}
                destinos={sucursales.filter((s) => s.tipo === 'restaurante' && s.id !== sucursalId)}
              />
            )}
        </div>

        {error && (
          <p role="alert" className="mt-4 rounded-lg bg-barro-claro px-4 py-3 text-sm text-barro-oscuro">
            {error}
          </p>
        )}

        <div className="mt-6 flex flex-col gap-2">
          <button className="btn-principal" disabled={guardando} onClick={() => guardar(true)}>
            {guardando ? 'Guardando…' : 'Guardar y poner a la venta'}
          </button>
          <button className="btn-neutro" disabled={guardando} onClick={() => guardar(false)}>
            Guardar como borrador
          </button>
          <p className="text-center text-xs text-salvia">
            Un borrador no le aparece al cliente hasta que lo pongas a la venta.
          </p>
        </div>
      </div>
    </div>
  );
}
